import React, { useState, useLayoutEffect, useRef, useEffect } from "react";
import { Grid, CircularProgress, Tab, Card, styled, alpha } from "@mui/material";
import { OcTabs } from "src/components/OcTabs/OcTabs";

// Style Imports
import { useTheme } from "@mui/styles";
import { Theme } from "@mui/material";

// Tabs
import { ManageBookings } from "./Tabs/ManageBookings";
import { ManageHotels } from "./TabHotels/ManageHotels";
import { ManageBookingErrors } from "./Tabs/ManageBookingErrors";
import HotelDashboard from "./Tabs/HotelDashboard";
import CrewTravelHome from "./Tabs/CrewTravelHome";

import DialogContent from "@mui/material/DialogContent";
import { DialogActions } from "@mui/material";
import { ModalTitle, ModalMessage, ModalButton } from "../../components/DialogModal/DialogModalComponents";
import { hasPermission, PolicyAction, PolicyResource, ClientSettingName } from "@vmosolutions/oc-interfaces";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { useSelector } from "react-redux";
import { getClientSetting } from "src/redux/actions/App.Actions";
import { useCancellablePromise } from "src/API/useCancellablePromise";

interface ICrewTravelWindowProps {
  path: any;
  id: any;
}

export interface IActiveModal {
  title: string;
  message: string;
  cancel: () => void;
  confirm: () => void;
  confirmLabel: string;
}

const OCBookingContainer = styled(Grid)(({ theme }) => ({
  height: "100%",
  flexWrap: "nowrap",
  "& .booking_cards": {
    height: "calc(100% - 10px)",
    minWidth: 900,
    backgroundColor: theme.custom.timeline.contextListBackground,
    "& .refresh_btn": {
      margin: 5,
      padding: "2px 8px",
      height: 20,
      fontSize: 10,
    },
  },
}));

const OCModalDialogue = styled(DialogContent)(({ theme }) => ({
  marginTop: -36,
  marginLeft: -10,
  display: "flex",
  justifyContent: "center",
  overflow: "hidden",
  alignItems: "center",
  position: "absolute",
  zIndex: 1300,
  width: "100%",
  height: "100%",
  backgroundColor: alpha(theme.palette.common.black, 0.5),
  "& .modal_content": {
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    flexDirection: "column",
    alignContent: "center",
    justifyContent: "center",
    borderRadius: 3,
    padding: 10,
    margin: 48,
    maxHeight: "calc(100% - 96px)",
    maxWidth: 600,
    position: "relative",
    boxShadow:
      "0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)",
    "& .slider-container": {
      padding: "0px 10px 0px 10px",
    },
    "& .modal_selection": {},
    "& .modal_status": {
      fontSize: 12,
      fontWeight: 300,
      color: theme.palette.primary.contrastText,
      paddingLeft: 25,
    },
  },
}));

/**
 * Administration and display of Crew Travel information.
 * @param props - properties
 */
export const CrewTravelWindow = (props: ICrewTravelWindowProps): JSX.Element => {
  const theme: Theme = useTheme();
  const { cancellablePromise } = useCancellablePromise();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading] = useState<boolean>(false);
  const [hasDashboard, setHasDashboard] = useState<boolean>(false);
  const { id, path } = props;
  const [activeModal, setActiveModal] = useState<IActiveModal>(null);
  const [tabValue, setTabValue] = useState<string>("CrewTravelHomeTab");
  const policies = useSelector((state: IRootState) => state.authentication?.attributes?.policies);
  useEffect((): void => {
    const getHotelClientSetting = async (): Promise<void> => {
      const hotelSetting = await cancellablePromise(getClientSetting(ClientSettingName.HotelBooking));
      setHasDashboard(
        hotelSetting?.setValue?.hotelDashboardEnabled === true &&
          hasPermission(policies, PolicyResource.HotelBookings, PolicyAction.Read)
      );
    };
    getHotelClientSetting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate height of the container on resize and send an assigned max height to table so that
  // it can scroll internally when viewer several results per page.
  const [tableMaxHeight, setTableMaxHeight] = useState<number>(0);
  useLayoutEffect(() => {
    setTimeout(() => {
      const { height } = containerRef?.current?.getBoundingClientRect() ?? {};
      const headerSize = 250;
      setTableMaxHeight(height - headerSize);
    }, 100);
  }, [path]);

  const handleSelectTab = (event: any, newValue: string): void => {
    setTabValue(newValue);
  };

  const tabContent = (): JSX.Element => {
    if (!!tabValue && typeof tabValue === "string") {
      switch (tabValue) {
        case "BookingsTab":
          return <ManageBookings id={id} path={path} tableMaxHeight={tableMaxHeight} />;
        case "BookingErrorsTab":
          return <ManageBookingErrors id={id} path={path} tableMaxHeight={tableMaxHeight} />;
        case "HotelsTab":
          return <ManageHotels id={id} setActiveModal={setActiveModal} />;
        case "HotelDashboardTab":
          return hasDashboard ? <HotelDashboard tableMaxHeight={tableMaxHeight} /> : <></>;
        case "CrewTravelHomeTab":
          return <CrewTravelHome setTabValue={handleSelectTab} hasDashboard={hasDashboard} />;
        default:
          return null;
      }
    }
    return null;
  };

  if (isLoading) {
    return (
      <Grid container>
        <Grid item style={{ flexGrow: 1 }}>
          <CircularProgress style={{ margin: "2rem" }} />
        </Grid>
      </Grid>
    );
  }
  return (
    <OCBookingContainer
      container
      direction="column"
      wrap="nowrap"
      ref={containerRef}
      style={{ height: "100%", width: "100%", overflowY: "auto", padding: "0px 10px" }}
    >
      <Grid item style={{ height: 32 }}>
        <OcTabs
          variant="scrollable"
          orientation="horizontal"
          scrollButtons="auto"
          value={tabValue}
          onChange={handleSelectTab}
        >
          <Tab wrapped={true} value="CrewTravelHomeTab" label="Crew Travel" classes={{ root: "form-tab" }} />
          {hasDashboard && (
            <Tab wrapped={true} value="HotelDashboardTab" label="Dashboard" classes={{ root: "form-tab" }} />
          )}
          <Tab wrapped={true} value="BookingsTab" label="Bookings" classes={{ root: "form-tab" }} />
          <Tab wrapped={true} value="BookingErrorsTab" label="Booking Errors" classes={{ root: "form-tab" }} />
          <Tab wrapped={true} value="HotelsTab" label="Hotels" classes={{ root: "form-tab" }} />
        </OcTabs>
      </Grid>

      <Grid item style={{ height: "calc(100% - 32px)" }}>
        <Card className="booking_cards">{tabContent()}</Card>
      </Grid>

      {activeModal && (
        <OCModalDialogue>
          <div className="modal_content" style={{ display: "flex", maxWidth: 500 }}>
            <ModalTitle title={activeModal.title} />
            <ModalMessage message={activeModal.message} />
            <DialogActions>
              <ModalButton label="Cancel" handler={activeModal.cancel}></ModalButton>
              <ModalButton label={activeModal.confirmLabel} handler={activeModal.confirm}></ModalButton>
            </DialogActions>
          </div>
        </OCModalDialogue>
      )}
    </OCBookingContainer>
  );
};
